import { Link } from "react-router-dom";
import { GlobeHemisphereWest, HouseLine, Robot, Plus, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export function InternationalComplianceHub({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-lg border border-amber-200 bg-amber-50/40 p-4 w-full hover:shadow-md hover:border-amber-300 transition-all duration-200"
      data-testid="widget-international-hub">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-amber-100 flex items-center justify-center">
            <GlobeHemisphereWest size={16} weight="fill" className="text-amber-700" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-amber-700">International</p>
            <p className="text-sm font-medium text-slate-900">Compliance hub</p>
          </div>
        </div>
        <span className="text-[10px] text-amber-700 flex items-center gap-0.5" data-testid="widget-international-link">
          Open <ArrowRight size={10} weight="bold" />
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-[10px] text-amber-900">
        <span className="rounded-sm bg-white/60 border border-amber-200 px-1.5 py-1">FBAR (FinCEN 114)</span>
        <span className="rounded-sm bg-white/60 border border-amber-200 px-1.5 py-1">FATCA · Form 8938</span>
        <span className="rounded-sm bg-white/60 border border-amber-200 px-1.5 py-1">Form 5472 / 5471</span>
        <span className="rounded-sm bg-white/60 border border-amber-200 px-1.5 py-1">ITIN · W-8BEN</span>
      </div>
      <p className="text-[10px] text-amber-800 mt-2">Live FBAR $10K &amp; FATCA $50K/$100K thresholds. Track every foreign account.</p>
    </button>
  );
}

export function RealEstateAssetTracker({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 w-full hover:shadow-md hover:border-indigo-300 transition-all duration-200"
      data-testid="widget-real-estate">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-indigo-100 flex items-center justify-center">
            <HouseLine size={16} weight="fill" className="text-indigo-700" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-indigo-700">Real estate</p>
            <p className="text-sm font-medium text-slate-900">1031 Exchange tracker</p>
          </div>
        </div>
        <span className="text-[10px] text-indigo-700 flex items-center gap-0.5" data-testid="widget-real-estate-link">
          Open <ArrowRight size={10} weight="bold" />
        </span>
      </div>
      <div className="space-y-1 text-[11px] text-indigo-900">
        <p className="flex items-center justify-between"><span>Identification deadline</span><span className="text-indigo-500">45 days</span></p>
        <p className="flex items-center justify-between"><span>Closing deadline</span><span className="text-indigo-500">180 days</span></p>
        <p className="flex items-center justify-between"><span>Countdown</span><span className="text-indigo-500">— live</span></p>
      </div>
      <p className="text-[10px] text-indigo-800 mt-2">Track relinquished sales, identify replacements, never miss a deadline.</p>
    </button>
  );
}

export function AIAutomationStatus({ documents }) {
  const total = documents?.length || 0;
  const filed = (documents || []).filter((d) => d.status && d.status !== "processing" && d.status !== "classification_failed" && d.status !== "needs_review").length;
  const rate = total ? Math.round((filed / total) * 100) : 0;
  return (
    <div className="rounded-lg border border-zinc-900 bg-zinc-900 text-white p-4" data-testid="widget-ai-status">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
            <Robot size={16} weight="fill" className="text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400">AI &amp; automation</p>
            <p className="text-sm font-medium">Status card</p>
          </div>
        </div>
        <Link to="/services/ai-finance" className="text-[10px] text-zinc-300 hover:text-white flex items-center gap-0.5" data-testid="widget-ai-link">
          Learn more <ArrowRight size={10} weight="bold" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <p className="text-lg font-heading font-semibold leading-none">{total}</p>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 mt-0.5">Docs seen</p>
        </div>
        <div>
          <p className="text-lg font-heading font-semibold leading-none">{filed}</p>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 mt-0.5">Auto-filed</p>
        </div>
        <div>
          <p className="text-lg font-heading font-semibold leading-none text-emerald-400">{rate}%</p>
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 mt-0.5">Accuracy</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3 text-[10px] text-emerald-300">
        <CheckCircle size={11} weight="fill" /> AR/AP agents standing by · reconciliation live
      </div>
    </div>
  );
}

export function RequestServiceCta() {
  return (
    <Link
      to="/services#request"
      data-testid="widget-request-service-cta"
      className="group flex items-center justify-between gap-3 rounded-lg border border-dashed border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-900 p-4 transition-colors duration-150">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-zinc-100 group-hover:bg-zinc-900 group-hover:text-white flex items-center justify-center transition-colors duration-150">
          <Plus size={18} weight="bold" />
        </div>
        <div>
          <p className="text-sm font-medium text-zinc-900">Request a new service</p>
          <p className="text-[11px] text-zinc-500">Pick from the catalog — your CPA is notified instantly.</p>
        </div>
      </div>
      <ArrowRight size={16} className="text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition-all duration-150" />
    </Link>
  );
}
